import { useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Sparkles, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errText } from "@/lib/api";

export const PasteMeetingDialog = ({ open, onOpenChange, onDraft }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const parse = async () => {
    if (text.trim().length < 20) return toast.error("Paste the whole message so I can read it");
    setBusy(true);
    try {
      const { data } = await api.post("/invites/parse", { text: text.trim() });
      if (!data.vc_link) toast.warning("No joining link found — paste the link if you have it");
      if (!data.found_date) toast.warning("No date in the message — today is filled in, please check");
      if (!data.found_time) toast.warning("No time in the message — set the time before saving");
      if (data.vc_link && data.found_date && data.found_time) {
        toast.success("Meeting read — check the entry before saving");
      }
      setText("");
      onOpenChange(false);
      onDraft(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[75vh] overflow-y-auto" data-testid="paste-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Video className="h-6 w-6" /> Paste a meeting
          </DialogTitle>
          <DialogDescription className="text-sm">
            Paste the email, invite or WhatsApp message. The subject, date, time, platform and joining
            link are picked out for you, and the case is matched to one of your matters where possible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label className="label-caps">Pasted text</Label>
          <Textarea
            data-testid="paste-meeting-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder={"Subject: VC hearing — …\n\nJoin Zoom Meeting\nhttps://us02web.zoom.us/j/…"}
            className="rounded-sm text-base font-mono"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-full h-11"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="paste-meeting-parse-btn"
            onClick={parse}
            disabled={busy}
            className="rounded-full h-11 px-6"
          >
            {busy ? (
              <>
                <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> Reading…
              </>
            ) : (
              <>
                <ClipboardPaste className="h-4 w-4 mr-2" /> Read the meeting
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
